import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useBookingProfessional } from '../../../hooks/useBookingProfessional';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { useChat } from '../../../context/ChatContext';
import axiosInstance from '../../../services/axiosInstance';
import { useCreateServerDay } from '../../../utils/calendarUtils';
import { BookingPanelProps } from '../types';

export const useBookingPanel = ({
    professionalUid,
    availability,
    valueDate,
    valueTime,
    onDateChange,
    onTimeChange,
    modality,
    consultTime,
    consultPrice,
    setIsBookingModalOpen,
    bookingMode = 'direct',
    requestSchedule,
    professionalName = '',
    professionalPicture = null,
    isOwnProfile = false,
    currency = 'UYU',
    pricingMode = 'single',
    initialPrice = null,
    initialDuration = null,
    followupPrice = null,
    followupDuration = null,
}: BookingPanelProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuthContext();
    const { showNotification } = useNotification();
    const { toggleChatDrawer, isChatDrawerOpen, setCurrentConversationId, loadConversations, notifyScheduleRequest, conversations, setTemporaryRecipient } = useChat();

    const currentLocale = i18n.language.startsWith('en') ? 'en' : 'es';

    useEffect(() => {
        dayjs.locale(currentLocale);
    }, [currentLocale]);

    const [sessionType, setSessionType] = useState<'initial' | 'followup' | null>(pricingMode === 'multi' ? 'initial' : null);

    const displayDuration = useMemo(() => {
        if (pricingMode === 'multi') {
            return sessionType === 'initial' ? initialDuration : followupDuration;
        }
        return consultTime;
    }, [pricingMode, sessionType, initialDuration, followupDuration, consultTime]);

    const {
        selectedDate,
        selectedTime,
        timeError,
        availabilityMap,
        timeOptions,
        handleDateChange: internalHandleDateChange,
        handleTimeChange: internalHandleTimeChange,
        validateTimeOnBlur,
        handleBookNow,
        shouldDisableDate,
        bookingModality,
        setBookingModality,
        modalityTooltipOpen,
        setModalityTooltipOpen
    } = useBookingProfessional({
        professionalUid,
        availability,
        controlledDate: valueDate === null || valueDate === undefined ? undefined : dayjs(valueDate),
        controlledTime: valueTime,
        consultTime: displayDuration ? Number(displayDuration) : undefined,
        modality,
        bookingMode,
        requestSchedule
    });

    const ServerDay = useCreateServerDay(availabilityMap);

    const isControlled = onDateChange !== undefined;
    const displayDate = isControlled ? (valueDate ? dayjs(valueDate) : null) : selectedDate;
    const displayTime = isControlled ? valueTime : selectedTime;

    const [dayTooltipOpen, setDayTooltipOpen] = useState(false);
    const [timeTooltipOpen, setTimeTooltipOpen] = useState(false)
    const [selectDayFirstTooltipOpen, setSelectDayFirstTooltipOpen] = useState(false);

    const displayPrice = useMemo(() => {
        if (pricingMode === 'multi') {
            return sessionType === 'initial' ? initialPrice : followupPrice;
        }
        return consultPrice;
    }, [pricingMode, sessionType, initialPrice, followupPrice, consultPrice]);

    const [requestPeriod, setRequestPeriod] = useState<string>('');
    const [requestNote, setRequestNote] = useState<string>('');
    const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);

    const [currentMonth, setCurrentMonth] = useState<Dayjs>(displayDate || dayjs());

    useEffect(() => {
        if (displayDate) {
            setCurrentMonth(displayDate);
        }
    }, [displayDate]);

    const calendarHeight = useMemo(() => {
        const daysInMonth = currentMonth.daysInMonth();
        const firstDayOfWeek = currentLocale === 'es' ? 1 : 0;
        const startDay = currentMonth.startOf('month').day();
        const emptyDays = (startDay + 7 - firstDayOfWeek) % 7;
        const rows = Math.ceil((emptyDays + daysInMonth) / 7);
        const baseHeight = 30;
        const headerHeight = 51;
        const dayFramePadding = 15; // 8px top + 7px bottom padding in MuiDayCalendar-root frame
        return headerHeight + (rows * baseHeight) + 10 + dayFramePadding;
    }, [currentMonth, currentLocale]);

    useEffect(() => {
        if (dayTooltipOpen) {
            const timer = setTimeout(() => setDayTooltipOpen(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [dayTooltipOpen]);

    useEffect(() => {
        if (timeTooltipOpen) {
            const timer = setTimeout(() => setTimeTooltipOpen(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [timeTooltipOpen]);

    useEffect(() => {
        if (selectDayFirstTooltipOpen) {
            const timer = setTimeout(() => setSelectDayFirstTooltipOpen(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [selectDayFirstTooltipOpen]);

    useEffect(() => {
        if (modalityTooltipOpen) {
            const timer = setTimeout(() => setModalityTooltipOpen(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [modalityTooltipOpen, setModalityTooltipOpen]);

    const handleDateUpdate = useCallback((newDate: Dayjs | null) => {
        if (onDateChange) onDateChange(newDate);
        else internalHandleDateChange(newDate);
        setDayTooltipOpen(false);
        setSelectDayFirstTooltipOpen(false);
    }, [onDateChange, internalHandleDateChange]);

    const handleTimeUpdate = useCallback((newTime: string) => {
        if (onTimeChange) onTimeChange(newTime);
        else internalHandleTimeChange(newTime);
        setTimeTooltipOpen(false);
        setSelectDayFirstTooltipOpen(false);
    }, [onTimeChange, internalHandleTimeChange]);

    const handleShowBookingModal = useCallback(() => {
        if (isOwnProfile) {
            showNotification(t('landing.professionalsDetail.bookingPanel.previewModeBookingError', 'Estás en modo vista previa. No puedes agendarte una cita a ti mismo.'), 'info');
            return;
        }

        if (!isAuthenticated) {
            showNotification(t('landing.professionalsDetail.bookingPanel.errorBooking'), 'error', 2000);
            navigate('/login');
            return;
        }

        if (!user?.type_account) {
            showNotification(
                t('landing.professionalsDetail.bookingPanel.completeProfileFirst', 'Por favor, selecciona si eres paciente o profesional para continuar.'),
                'warning',
                4000
            );
            navigate('/selection-type-account');
            return;
        }

        let hasError = false;

        if (!displayDate) {
            setDayTooltipOpen(true);
            hasError = true;
        }

        if (modality === 'Presencial/Virtual' && !bookingModality) {
            setModalityTooltipOpen(true);
            hasError = true;
        }

        if (!displayTime) {
            setTimeTooltipOpen(true);
            hasError = true;
        }

        if (hasError) return;

        const isSave = handleBookNow(sessionType);
        if (isSave && setIsBookingModalOpen) {
            setIsBookingModalOpen(true);
        }
    }, [isOwnProfile, isAuthenticated, user?.type_account, displayDate, modality, bookingModality, displayTime, handleBookNow, setIsBookingModalOpen, navigate, showNotification, t, setModalityTooltipOpen, bookingMode]);

    const handleRequestSchedule = useCallback(async () => {
        if (isOwnProfile) {
            showNotification(t('landing.professionalsDetail.bookingPanel.previewModeBookingError', 'Estás en modo vista previa. No puedes agendarte una cita a ti mismo.'), 'info');
            return;
        }

        if (!isAuthenticated) {
            showNotification(t('landing.professionalsDetail.bookingPanel.errorBooking'), 'error', 2000);
            navigate('/login');
            return;
        }

        if (!user?.type_account) {
            showNotification(
                t('landing.professionalsDetail.bookingPanel.completeProfileFirst', 'Por favor, selecciona si eres paciente o profesional para continuar.'),
                'warning',
                4000
            );
            navigate('/selection-type-account');
            return;
        }

        let hasError = false;

        if (!displayDate) {
            setDayTooltipOpen(true);
            hasError = true;
        }

        if (modality === 'Presencial/Virtual' && !bookingModality) {
            setModalityTooltipOpen(true);
            hasError = true;
        }

        if (!requestPeriod) {
            setTimeTooltipOpen(true); // Reusing timeTooltipOpen for period
            hasError = true;
        }

        if (hasError) return;

        setIsSubmittingRequest(true);
        try {
            const response = await axiosInstance.post('/api/chat/booking/request', {
                professionalId: professionalUid,
                date: displayDate!.format('YYYY-MM-DD'),
                period: requestPeriod,
                modality: bookingModality || modality,
                note: requestNote,
                consultPrice: displayPrice || 0,
                sessionType: sessionType,
                currency: currency
            });
            showNotification(t('landing.professionalsDetail.bookingPanel.requestSuccess', 'Solicitud de horario enviada con éxito'), 'success');

            if (response.data?.chatMessage?.conversationId) {
                setCurrentConversationId(response.data.chatMessage.conversationId);
                setTemporaryRecipient(null);
                loadConversations(); // Trigger a reload of the conversation list to ensure it's up to date
                
                // Notify the professional in real time
                notifyScheduleRequest(professionalUid, response.data.chatMessage.conversationId, response.data.chatMessage);
            }

            if (!isChatDrawerOpen) {
                toggleChatDrawer();
            }
        } catch (error) {
            console.error('Error requesting schedule:', error);
            showNotification(t('landing.professionalsDetail.bookingPanel.requestError', 'Error al enviar la solicitud'), 'error');
        } finally {
            setIsSubmittingRequest(false);
        }
    }, [isOwnProfile, isAuthenticated, user?.type_account, displayDate, modality, bookingModality, requestPeriod, requestNote, navigate, showNotification, t, professionalUid, toggleChatDrawer, isChatDrawerOpen, displayPrice, sessionType, currency, setCurrentConversationId, loadConversations, notifyScheduleRequest, setTemporaryRecipient]);

    const handleOpenChat = useCallback(() => {
        if (isOwnProfile) {
            showNotification(t('landing.professionalsDetail.bookingPanel.previewModeBookingError', 'Estás en modo vista previa. No puedes enviarte un mensaje a ti mismo.'), 'info');
            return;
        }

        if (!isAuthenticated) {
            navigate('/login');
            showNotification(t('landing.professionalsDetail.bookingPanel.authRequired', 'Debe iniciar sesión para enviar mensajes'), 'warning');
            return;
        }

        const existingConv = conversations.find(
            c => c.otherUser.uid === professionalUid || c.otherUser.id?.toString() === professionalUid?.toString()
        );

        if (existingConv) {
            setCurrentConversationId(existingConv.id);
            setTemporaryRecipient(null);
        } else {
            const names = professionalName.trim().split(' ');
            const firstName = names[0] || 'Profesional';
            const lastName = names.slice(1).join(' ') || '';
            
            let picUrl: string | null = null;
            if (professionalPicture) {
                if (typeof professionalPicture === 'string') {
                    picUrl = professionalPicture;
                } else if (professionalPicture instanceof File) {
                    picUrl = URL.createObjectURL(professionalPicture);
                }
            }

            setTemporaryRecipient({
                id: professionalUid,
                firstName,
                lastName,
                profilePicture: picUrl
            });
            setCurrentConversationId(-1);
        }

        if (!isChatDrawerOpen) {
            toggleChatDrawer();
        }
    }, [isOwnProfile, isAuthenticated, navigate, showNotification, t, conversations, professionalUid, setCurrentConversationId, setTemporaryRecipient, professionalName, professionalPicture, isChatDrawerOpen, toggleChatDrawer]);

    return {
        t,
        currentLocale,
        displayDate,
        displayTime,
        timeOptions,
        timeError,
        availabilityMap,
        ServerDay,
        dayTooltipOpen,
        timeTooltipOpen,
        selectDayFirstTooltipOpen,
        bookingModality,
        modalityTooltipOpen,
        setDayTooltipOpen,
        setTimeTooltipOpen,
        setSelectDayFirstTooltipOpen,
        setBookingModality,
        setModalityTooltipOpen,
        handleDateUpdate,
        handleTimeUpdate,
        handleShowBookingModal,
        shouldDisableDate,
        validateTimeOnBlur,
        calendarHeight,
        currentMonth,
        setCurrentMonth,
        requestPeriod,
        setRequestPeriod,
        requestNote,
        setRequestNote,
        handleRequestSchedule,
        handleOpenChat,
        isSubmittingRequest,
        sessionType,
        setSessionType,
        displayPrice,
        displayDuration
    };
};
